import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, getPushStatus, isPushSupported } from "@/lib/push";
import { api } from "@/lib/api";

export default function PushToggle() {
  const [status, setStatus] = useState("prompt");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!(await isPushSupported())) { setStatus("unsupported"); return; }
      setStatus(await getPushStatus());
    })();
  }, []);

  const on = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled");
      setStatus("subscribed");
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    } finally { setBusy(false); }
  };

  const off = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push notifications disabled");
      setStatus("prompt");
    } finally { setBusy(false); }
  };

  const test = async () => {
    try {
      await api.post("/push/test");
      toast("Test sent — check your notifications.");
    } catch (e) { toast.error("Test failed"); }
  };

  if (status === "unsupported") {
    return (
      <div data-testid="push-unsupported" className="flex items-center gap-2 text-xs text-[color:var(--umg-text-2)]">
        <BellOff className="w-4 h-4" /> Browser push not supported here.
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div data-testid="push-denied" className="flex items-center gap-2 text-xs text-[color:var(--umg-text-2)]">
        <BellOff className="w-4 h-4" /> Notifications blocked in browser settings.
      </div>
    );
  }

  if (status === "subscribed") {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-[#4F7942] font-semibold">
          <BellRing className="w-4 h-4" /> Push notifications on
        </span>
        <Button data-testid="push-test" variant="ghost" size="sm" onClick={test} className="text-xs h-8">Send test</Button>
        <Button data-testid="push-disable" variant="ghost" size="sm" onClick={off} disabled={busy} className="text-xs h-8">Turn off</Button>
      </div>
    );
  }

  return (
    <Button
      data-testid="push-enable"
      onClick={on}
      disabled={busy}
      variant="outline"
      size="sm"
      className="rounded-full border-[color:var(--umg-border)]"
    >
      <Bell className="w-4 h-4 mr-1.5" /> Enable notifications
    </Button>
  );
}
